<template>
  <div class="m-b-15">
    <Field
      v-if="rpc.enabled"
      :label="$t('PREDICTIVE_ROUTING_LABEL')"
      :has-tooltip="true"
      :tooltip-text="$t('PREDICTIVE_ROUTING_TT')"
    >
      <template #element>
        <div class="col-sm-5 col-md-3">
          <div class="r-form display-flex">
            <ValidationProvider
              v-slot="{ classes }"
              :rules="isPredictiveRoutingConfigRequired ? 'required' : ''"
              slim
            >
              <SearchableDropdown
                v-model="configDropdownValue"
                :config="predictiveRoutingOptions"
                :searchable="true"
                :clearable="false"
                :class="classes"
                :rules="isPredictiveRoutingConfigRequired ? 'required' : ''"
                class="m-0 r-form__searchable-dropdown"
                @change="onConfigChange"
              />
            </ValidationProvider>
            <button
              type="button"
              class="r-form__input-action-button"
              :class="[configDropdownValue ? 'm-r-12' : 'm-r-20']"
              @click="triggerGetPredictiveRouting"
            >
              <i
                class="fa fa-refresh"
                :class="{'fa-spin': isLoadingPredictiveRouting }"
                :title="$t('PREDICTIVE_ROUTING_REFRESH')"
              />
            </button>

            <!-- Predictive Routing Detail link -->
            <a
              v-if="configDropdownValue"
              class="m-r-20 m-t-8"
              :href='`#/dashboard/predictive-routing/${configDropdownValue}`'
              target="_blank"
            >
              <i
                class="fa fa-external-link"
                :title="$t('PREDICTIVE_ROUTING_VIEW_SELECTED')"
              />
            </a>

            <button
              type="button"
              class="btn btn-primary btn-sm"
              @click="createNew"
            >
              <i class="fa fa-plus" /> {{ $t('NEW') }}
            </button>
            <label
              v-if="isPredictiveRoutingConfigRequired"
              v-t="'REQUIRED'"
              class="form-group__label-required-manual m-l-15"
              :class="{'invalid': !rpc.id}"
            />
          </div>
        </div>
      </template>
    </Field>
    <div v-if="isConfigMigrated" class="row">
      <div class="col-sm-offset-3 col-sm-8 col-md-5">
        <div v-html="$t('MIGRATED_SETTINGS_MESSAGE')" class="bs-callout bs-callout-sm bs-callout-warning m-t-5" />
      </div>
    </div>
    <Field
      v-if="showPriorityBump"
      :label="$t('PRIORITY_BUMP')"
      :has-tooltip="true"
      :tooltip-text="$t('PRIORITY_BUMP_TT')"
    >
      <template #element>
        <div class="col-sm-8 col-md-5">
          <vue-slider
            v-model="currentPriorityBump"
            class="vue-slider-v1 flex--grow"
            :tooltip="'active'"
            :height="15"
            :data="bumpData"
            :data-value="'id'"
            :data-label="'name'"
            :marks="bumpMarks"
            @change="onPriorityBumpChange"
          >
            <template v-slot:dot>
              <div class="vue-slider-dot-handle">
                <i class="fa fa-ellipsis-v" aria-hidden="true" />
              </div>
            </template>
          </vue-slider>
        </div>
      </template>
    </Field>
    <ConfirmationModal
      v-if="showModal"
      :title="$t('PREDICTIVE_ROUTING_CREATE_HEADER')"
      :show-confirm-button="false"
      :show-cancel-button="false"
      :custom-classes="'erpc-settings-form-modal'"
      @close="showModal = false"
    >
      <ERPCSettingsForm
        :show-loader="createPredictiveRoutingConfigStatus.status === 'pending'"
        @submit="onSubmitCreateNew"
        @cancel="showModal = false"
      />
    </ConfirmationModal>
  </div>
</template>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { bumpData, bumpMarks } from '../helpers/constants';
import { mapGetters, mapState, mapActions } from 'vuex';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS } from '@/store/helpers';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import ERPCSettingsForm from '@/core/components/RPCSettings/ERPCSettings/ERPCSettingsForm';

const predictiveRoutingOptionsConfig = {
  reduce: (option) => option.id,
  label: 'name',
};

export default {
  name: 'ERPCSettings',
  components: {
    SearchableDropdown,
    ConfirmationModal,
    nzToggle,
    ERPCSettingsForm
  }, 

  props: {
    entityType: {
      type: String,
      default: 'account'
    },
    rpc: {
      type: Object,
      default: () => {
        return {};
      }
    },
    priorityBump: {
      type: String,
      default: '0'
    }
  },
  
  data() {
    return {
      showModal: false,
      isLoadingPredictiveRouting: false,
      bumpData: bumpData,
      bumpMarks: bumpMarks,
    };
  },

  computed: {
    ...mapState('predictiveRouting', ['predictiveRouting']),
    ...mapGetters('predictiveRouting', ['createPredictiveRoutingConfigStatus']),

    predictiveRoutingOptions() {
      return {
        options: this.predictiveRouting,
        ...predictiveRoutingOptionsConfig,
        placeholder: this.$t('PREDICTIVE_ROUTING_PLACEHOLDER')
      };
    },
    isPredictiveRoutingConfigRequired() {
      return this.entityType === 'target' || this.entityType === 'buyer' || this.entityType === 'ping-tree-target';
    },
    showPriorityBump() {
      return this.entityType === 'target' || this.entityType === 'buyer' || this.entityType === 'ping-tree-target';
    },
    isConfigMigrated() {
      return (this.rpc?.id.substr(0, 2) === 'TA' || this.rpc?.id.substr(0, 2) === 'CA' || this.rpc?.id.substr(0, 2) === 'PI' || this.rpc?.id.substr(0, 2) === 'BU') && this.rpc?.id.substr(-4) === 'LINK';
    },
    currentPriorityBump: {
      get() {
        return this.priorityBump;
      },
      set(newValue) {
        return newValue;
      }
    },
    configDropdownValue: {
      get() {
        return this.isConfigMigrated ? 'Choose a setting' : this.rpc.id;
      },
      set(newValue) {
        this.rpc.id = newValue;
      }
    }
  },

  mounted() {
    this.getPredictiveRouting();
  },
  methods: {
    ...mapActions('predictiveRouting', [
      'getPredictiveRouting',
      'createPredictiveRoutingConfig'
    ]),

    async triggerGetPredictiveRouting() {
      this.isLoadingPredictiveRouting = true;
      await this.getPredictiveRouting();
      setTimeout(() => {
        this.isLoadingPredictiveRouting = false;
      }, 1200);
    },

    createNew() {
      this.showModal = true;
    },

    async onSubmitCreateNew(config) {
      const newConfig = await this.createPredictiveRoutingConfig(config);
      if (this.createPredictiveRoutingConfigStatus.status === STATUS_SUCCESS) {
        this.showModal = false;
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_CREATE_SUCCESS'));
        this.onConfigChange(newConfig.rpcSettingItem.id);
      }
      if (this.createPredictiveRoutingConfigStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createPredictiveRoutingConfigStatus.error);
      }
    },

    onConfigChange(value) {
      this.$emit('change', value);
    },

    onPriorityBumpChange(value) {
      this.$emit('priorityBumpChange', value);
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>